import React from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useTranslation } from "react-i18next";

import { getProductImageUrl } from "../../../../commons/libs/resource-paths";
import { Product, ProductKey, StandaloneProductVariant } from "../../../../commons/specs/product";
import { IconSmallZoomIn } from "../../../../resources/icons";
import zoomAnimationData from "../../../../resources/images/lottie/zoom-animation.json";
import CircularButton from "../../components/CircularButton/CircularButton";
import Icon from "../../components/Icon/Icon";
import ProductDetailsImage from "../../components/ProductDetailsImage/ProductDetailsImage";
import Tooltip from "../../components/Tooltip/Tooltip";
import ZoomImageModal from "../../components/ZoomImageModal/ZoomImageModal";
import { useSettings } from "../../libs/queries/use-settings";
import { useCommonAppDispatcher, useCommonAppSelector } from "../../store/hooks";
import ModalContainerWithStatePartial from "../Modal/ModalContainerWithStatePartial";
import ProductTitlePartial from "../partials/ProductTitlePartial";
import { useTrackingContext } from "../utils/tracking-context";

const zoomAnimationDataString = JSON.stringify(zoomAnimationData);

interface Props {
  product: Product;
  productVariant: StandaloneProductVariant;
  isProductContentAsideOpen?: boolean;
}

const ProductDetailsImagePartial = ({ product, productVariant, isProductContentAsideOpen = false }: Props) => {
  const showProductDetailsZoomNotice = useCommonAppSelector(state => state.session.showProductDetailsZoomNotice);
  const [dispatch, actions] = useCommonAppDispatcher();

  const hideProductDetailsZoomNotice = () => dispatch(actions.session.hideProductDetailsZoomNotice());
  const openButtonRef = React.useRef<HTMLButtonElement>(null);
  const { t } = useTranslation(["commons"]);
  const { mixpanel } = useTrackingContext();
  const settings = useSettings();

  return (
    <ModalContainerWithStatePartial
      onOpenChange={isOpen => {
        const variantId = productVariant[ProductKey.VariantId];

        if (isOpen) {
          mixpanel?.trackProductDetailsImageZoomOpened(product, variantId);
        } else {
          mixpanel?.trackProductDetailsImageZoomClosed(product, variantId);
        }
      }}
      modal={(_isOpen, close) => (
        <ZoomImageModal
          openButton={openButtonRef.current}
          imageUrl={getProductImageUrl(productVariant)}
          notice={
            <Tooltip
              variant="accent"
              maxWidth="s"
              content={t("productDetailsImagePartialProductDetails.zoomNotice")}
              openOnMount
              isCloseable
              openOnMountDelay={300}
              shouldCloseAutomatically={false}
              onClose={hideProductDetailsZoomNotice}
            >
              <DotLottieReact loop autoplay data={zoomAnimationDataString} style={{ height: 240, width: 240 }} />
            </Tooltip>
          }
          onClose={close}
          showNotice={showProductDetailsZoomNotice}
          onHideNotice={hideProductDetailsZoomNotice}
        />
      )}
    >
      {(isOpen, open) => (
        <ProductDetailsImage
          isProductContentAsideOpen={isProductContentAsideOpen}
          imageUrl={getProductImageUrl(productVariant)}
          onClick={open}
          action={
            <CircularButton ref={openButtonRef} variant={isOpen ? "accent" : "primary"} elevated onClick={open}>
              <Icon source={IconSmallZoomIn} />
            </CircularButton>
          }
          productTitle={
            <ProductTitlePartial
              product={product}
              align={isProductContentAsideOpen ? "left" : "center"}
              currency={settings.initialized.currency}
              customization={settings.initialized.customization}
              assortmentPriceSettings={settings.assortmentPriceSettings}
            />
          }
        />
      )}
    </ModalContainerWithStatePartial>
  );
};

export default ProductDetailsImagePartial;
